import { useEffect, useRef } from 'react';
import { useGetCurrencyStore, useSetCurrencyStore } from '../../../context/CurrencyContext';
import { useDisplayedCurrencyCode } from '../../hooks/pricing/useCurrencyDetails';
import { isAvailableCurrencyCode } from '../../utils/typeguards';
import { useQueryParamCurrency } from '../../utils/urls/appQueryParamUtils';

/**
 * Hook to handle initial currency synchronization between query params and currency store
 * This handles the initial setup when the component first mounts
 */
export const useCurrencyInitialization = () => {
  const setCurrencyStore = useSetCurrencyStore();
  const {
    lockedCurrencyCode,
    displayedCurrencyCode
  } = useGetCurrencyStore(store => store);
  const {
    queryParamCurrency,
    setQueryParamCurrency
  } = useQueryParamCurrency();
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && queryParamCurrency === null) {
      // No query param exists, initialize with displayed currency
      if (isAvailableCurrencyCode(displayedCurrencyCode)) {
        setQueryParamCurrency(displayedCurrencyCode);
        hasInitialized.current = true;
      }
    } else if (!hasInitialized.current && lockedCurrencyCode && isAvailableCurrencyCode(lockedCurrencyCode) && lockedCurrencyCode !== queryParamCurrency) {
      // Locked currency exists and differs from query param, update to locked
      setQueryParamCurrency(lockedCurrencyCode);
      hasInitialized.current = true;
    } else if (!hasInitialized.current && queryParamCurrency && isAvailableCurrencyCode(queryParamCurrency) && queryParamCurrency !== displayedCurrencyCode) {
      // Query param exists and differs from displayed currency, update store to match query param
      const showCurrencyCodeUnit = queryParamCurrency === 'USD';
      setCurrencyStore({
        displayedCurrencyCode: queryParamCurrency,
        showCurrencyCodeUnit
      });
      hasInitialized.current = true;
    } else if (!hasInitialized.current) {
      // No changes needed, just mark as initialized
      hasInitialized.current = true;
    }
  }, [displayedCurrencyCode, lockedCurrencyCode, queryParamCurrency, setQueryParamCurrency, setCurrencyStore]);
  return hasInitialized;
};

/**
 * Hook to handle query parameter changes after initialization
 * This handles cases like navigating to a new page where the query param changes
 */
export const useCurrencyQueryParamSync = hasInitialized => {
  const setCurrencyStore = useSetCurrencyStore();
  const displayedCurrencyCode = useDisplayedCurrencyCode();
  const {
    queryParamCurrency
  } = useQueryParamCurrency();
  useEffect(() => {
    if (hasInitialized.current && queryParamCurrency && isAvailableCurrencyCode(queryParamCurrency)) {
      // Query param exists and is valid, sync store if it differs from displayed currency
      if (queryParamCurrency !== displayedCurrencyCode) {
        const showCurrencyCodeUnit = queryParamCurrency === 'USD';
        setCurrencyStore({
          displayedCurrencyCode: queryParamCurrency,
          showCurrencyCodeUnit
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParamCurrency, displayedCurrencyCode, setCurrencyStore]);
};